"use client";

import {Button} from '@mantine/core';
import {IconArrowBackUp, IconLogout, IconShieldCheck} from '@tabler/icons-react';
import styles from './IdentityControls.module.css';
import {signOut, useSession} from 'next-auth/react';
import {useEffect, useRef, useState} from 'react';
import {shouldRefreshIdentity} from '@/lib/auth/identity-refresh';

const identityEvent = 'tcc-identity-changed';
export function announceIdentityChange() {
    try { localStorage.setItem(identityEvent, `${Date.now()}-${Math.random()}`); } catch { /* Session polling remains available. */ }
}

export default function IdentityControls() {
    const {data: session, status, update} = useSession();
    const [busy, setBusy] = useState(false);
    const lock = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const previous = useRef<string>();
    const user = session?.user;
    useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key === identityEvent) window.location.reload();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    useEffect(() => {
        if (status === 'loading') return;
        const identity = `${user?.id || ''}:${!!user?.impersonating}`;
        if (shouldRefreshIdentity(previous.current, identity, window.location.pathname, lock.current)) window.location.reload();
        previous.current = identity;
    }, [status, user?.id, user?.impersonating]);
    useEffect(() => {
        if (!user?.impersonating || !user.impersonationExpiresAt) return;
        const timeout = window.setTimeout(() => {
            if (window.location.pathname !== '/impersonation-expired') window.location.assign('/impersonation-expired');
        }, Math.max(0, user.impersonationExpiresAt - Date.now()));
        return () => window.clearTimeout(timeout);
    }, [user?.impersonating, user?.impersonationExpiresAt]);

    const restore = async () => {
        if (lock.current) return;
        lock.current = true;
        setBusy(true);
        setError(null);
        try {
            const result = await update({action: 'restoreAdministrator'});
            if (!result || result.user.impersonating || result.user.role !== 'admin' || result.user.impersonationError) {
                throw new Error(result?.user.impersonationError || '返回管理者失敗，請重新登入');
            }
            announceIdentityChange();
            window.location.assign('/manager/accounts');
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : '返回管理者失敗');
            setBusy(false);
            lock.current = false;
        }
    };
    if (!user?.impersonating) return null;
    return <section className={styles.bar} aria-label="代登入狀態">
        <div className={styles.inner}>
            <div className={styles.identity}>
                <div className={styles.icon} aria-hidden="true"><IconShieldCheck size={25} stroke={1.7}/></div>
                <div className={styles.details}>
                    <div className={styles.heading}>
                        <span className={styles.badge}>{user.impersonationExpired ? '代登入已到期' : '代登入模式'}</span>
                        <span className={styles.account}>{user.username}</span>
                    </div>
                    <p className={styles.description}>{user.impersonationExpired ? '本次代登入已結束，請返回管理者以繼續操作。' : '您正以此使用者的身分瀏覽與操作，完成後可返回管理者。'}</p>
                </div>
            </div>
            <div className={styles.actions}>
                <Button className={styles.returnButton} variant="white" color="red" radius="md" size="sm"
                        leftSection={<IconArrowBackUp size={17} aria-hidden="true"/>}
                        loading={busy} onClick={restore}>返回管理者</Button>
                <Button className={styles.logoutButton} variant="transparent" radius="md" size="sm"
                        leftSection={<IconLogout size={17} aria-hidden="true"/>}
                        disabled={busy} onClick={() => signOut({callbackUrl: '/login'})}>登出</Button>
            </div>
            {error && <div className={styles.error} role="alert">{error}</div>}
        </div>
    </section>;
}
